'use client'

import { useEffect, useRef, useState } from 'react'
import { getResendOtpInformation, sendOtpMessage, verifyOtp } from '~/actions/otp'

const toCountdownSeconds = (response: Awaited<ReturnType<typeof getResendOtpInformation>>): number => {
  if (!response) return 0
  const s = Math.ceil((new Date(response.when).getTime() - Date.now()) / 1000)
  return s > 0 ? s : 0
}

export const useScheduleOtp = (phone: string) => {
  const [otpStatus, setOtpStatus] = useState<'idle' | 'loading' | 'validated' | 'invalid'>('idle')
  const [resendCountdown, setResendCountdown] = useState<number | null>(null)
  const canResend = resendCountdown === 0
  const mountedRef = useRef(true)

  useEffect(() => {
    mountedRef.current = true
    return () => {
      mountedRef.current = false
    }
  }, [])

  // Guards resendOtp path: if the user navigates away while a resend is in
  // flight, mountedRef.current is false and the state update is skipped.
  const refreshCountdown = async () => {
    const response = await getResendOtpInformation(phone)
    if (!mountedRef.current) return
    setResendCountdown(toCountdownSeconds(response))
  }

  const onOtpComplete = async (code: string) => {
    setOtpStatus('loading')
    setOtpStatus((await verifyOtp(phone, code)) ? 'validated' : 'invalid')
  }

  const resendOtp = async () => {
    await sendOtpMessage(phone)
    await refreshCountdown()
  }

  // Guards mount/phone-change path with a per-run cancelled flag so that
  // a stale fetch from a previous phone value cannot update state.
  useEffect(() => {
    let cancelled = false
    getResendOtpInformation(phone).then((response) => {
      if (!cancelled) setResendCountdown(toCountdownSeconds(response))
    })
    return () => {
      cancelled = true
    }
  }, [phone])

  useEffect(() => {
    if (!resendCountdown) return
    const timeout = setTimeout(() => setResendCountdown(resendCountdown - 1), 1000)
    return () => clearTimeout(timeout)
  }, [resendCountdown])

  const resetOtpStatus = () => setOtpStatus('idle')

  return { otpStatus, canResend, resendCountdown, onOtpComplete, resendOtp, resetOtpStatus }
}
